import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/shared/Common";
import DocumentUploader from "@/components/shared/DocumentUploader";
import { ShieldCheck, ShieldAlert } from "lucide-react";

const KIND_OPTIONS = [
  ["doctor_verification_id", "Government-issued ID"],
  ["doctor_verification_registration", "Registration certificate"],
  ["doctor_verification_insurance", "Professional indemnity insurance"],
  ["doctor_verification_qualifications", "Qualification certificates"],
  ["doctor_verification_other", "Other supporting document"],
];

export default function DoctorVerification() {
  const [me, setMe] = useState(null);
  useEffect(() => { api.get("/doctor/me").then(r => setMe(r.data)); }, []);
  if (!me) return <div className="text-center text-[#475569]">Loading…</div>;

  const isVerified = me.verification_status === "verified";

  return (
    <div className="space-y-6">
      <div><div className="cm-eyebrow mb-2">Verification</div><h1 className="cm-h2">Credentials &amp; documents</h1></div>
      <div className={`cm-card ${isVerified ? "border-[#3daa6e]/30 bg-[#eaf7f0]" : "border-[#d97706]/30 bg-[#fffbeb]"}`}>
        <div className="flex items-start gap-3">
          {isVerified ? <ShieldCheck className="text-[#3daa6e]"/> : <ShieldAlert className="text-[#d97706]"/>}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <div className={`font-semibold ${isVerified ? "text-[#065f46]" : "text-[#92400e]"}`}>Status</div>
              <StatusBadge status={me.verification_status || "pending"}/>
            </div>
            <div className={`text-sm mt-1 ${isVerified ? "text-[#065f46]/80" : "text-[#92400e]/80"}`}>
              {isVerified
                ? "You're verified. Your profile is visible to patients and you can accept bookings."
                : "Your profile isn't visible to patients until we complete verification. Please upload the required documents below."}
            </div>
            {me.verification_note && <div className="text-sm mt-2 italic">Reviewer note: {me.verification_note}</div>}
          </div>
        </div>
      </div>

      <div className="cm-card grid sm:grid-cols-2 gap-4">
        <Info label="Full name" value={`Dr. ${me.first_name} ${me.last_name}`}/>
        <Info label="Specialty" value={(me.specialty||"").replace("_"," ")}/>
        <Info label="Registration body" value={me.registration_body || "—"}/>
        <Info label="Registration number" value={me.registration_number || "—"}/>
        <Info label="Qualifications" value={me.qualifications || "—"}/>
        <Info label="Years experience" value={me.years_experience ?? "—"}/>
        <Info label="Professional location" value={me.professional_location || "—"}/>
        <Info label="Languages" value={(me.languages||[]).join(", ") || "—"}/>
      </div>

      <DocumentUploader
        title="Verification documents"
        description="Upload each required document — ID, registration, indemnity insurance, and qualification certificates. Files are encrypted and only visible to CAREMatch reviewers."
        showKindPicker
        kindOptions={KIND_OPTIONS}
      />

      <div className="cm-card">
        <h3 className="font-semibold text-[#1a2b4a] mb-3">Required documents checklist</h3>
        <ul className="text-sm text-[#475569] space-y-2">
          <li>· Government-issued ID (passport / driving licence)</li>
          <li>· Medical council registration document (GMC or equivalent)</li>
          <li>· Certificate of Good Standing (if applicable)</li>
          <li>· Professional indemnity insurance</li>
        </ul>
      </div>
    </div>
  );
}
const Info = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-wider text-[#94a3b8] font-semibold">{label}</div>
    <div className="text-sm font-medium text-[#1a2b4a] capitalize mt-0.5">{value}</div>
  </div>
);
